if (!customElements.get('tooto-carousel')) {
  class TootoCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.retryFrame = null;
      this.retryCount = 0;
      this.isBound = false;
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
    }

    connectedCallback() {
      if (!this.isBound) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
        document.addEventListener('shopify:section:unload', this.handleSectionUnload);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        this.addEventListener('click', this.handleClick);
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
        this.isBound = true;
      }

      this.initialize();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.isBound = false;
      this.destroy();
    }

    initialize() {
      if (this.swiper) return;

      const slider = this.querySelector('.tooto-carousel__slider');
      const slides = slider?.querySelectorAll('.swiper-wrapper > .tooto-carousel-item');

      if (!slider || !slides?.length) {
        this.classList.add('is-empty');
        return;
      }

      this.classList.remove('is-empty');

      if (slides.length === 1) {
        this.classList.add('is-static');
        return;
      }

      this.classList.remove('is-static');

      if (typeof window.Swiper !== 'function') {
        if (this.retryCount < 180) {
          this.retryCount += 1;
          this.retryFrame = requestAnimationFrame(() => this.initialize());
        }
        return;
      }

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const pagination = this.querySelector('.tooto-carousel__pagination');
      const previousButton = this.querySelector('.tooto-carousel__navigation--previous');
      const nextButton = this.querySelector('.tooto-carousel__navigation--next');
      const loopEnabled = this.dataset.loop === 'true' && slides.length > 1;
      const config = {
        slidesPerView: 'auto',
        centeredSlides: true,
        spaceBetween: Number(this.dataset.gapMobile) || 0,
        speed: reducedMotion ? 0 : Number(this.dataset.speed) || 500,
        loop: loopEnabled,
        loopAdditionalSlides: loopEnabled ? Math.max(2, Math.ceil(slides.length / 2)) : 0,
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        roundLengths: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        mousewheel: {
          enabled: true,
          forceToAxis: true,
          releaseOnEdges: true,
        },
        a11y: {
          enabled: true,
          prevSlideMessage: this.dataset.previousLabel,
          nextSlideMessage: this.dataset.nextLabel,
        },
        breakpoints: {
          750: {
            spaceBetween: Number(this.dataset.gap) || 0,
          },
        },
      };

      if (pagination) {
        const loadSlideLabel = this.dataset.loadSlideLabel || 'Load slide';
        config.pagination = {
          el: pagination,
          clickable: true,
          renderBullet(index, className) {
            return `<button class="${className} tooto-carousel__indicator" type="button" aria-label="${loadSlideLabel} ${index + 1}"></button>`;
          },
        };
      }

      if (previousButton && nextButton) {
        config.navigation = {
          prevEl: previousButton,
          nextEl: nextButton,
        };
      }

      if (this.dataset.autoplay === 'true' && !reducedMotion) {
        config.autoplay = {
          delay: Number(this.dataset.autoplayDelay) || 3000,
          disableOnInteraction: false,
          pauseOnMouseEnter: this.dataset.pauseOnHover === 'true',
        };
      }

      this.retryCount = 0;
      this.swiper = new window.Swiper(slider, config);
    }

    destroy() {
      if (this.retryFrame) {
        cancelAnimationFrame(this.retryFrame);
        this.retryFrame = null;
      }

      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.destroy(true, true);
      }

      this.swiper = null;
      this.retryCount = 0;
    }

    handleBlockSelect(event) {
      const selectedItem = event.target.closest?.('.tooto-carousel-item');
      if (!selectedItem || !this.contains(selectedItem) || !this.swiper) return;

      const originalSlides = Array.from(
        this.querySelectorAll('.swiper-wrapper > .tooto-carousel-item:not(.swiper-slide-duplicate)')
      );
      const slideIndex = originalSlides.findIndex((slide) => slide.dataset.blockId === selectedItem.dataset.blockId);

      if (slideIndex < 0) return;

      if (this.swiper.params.loop && typeof this.swiper.slideToLoop === 'function') {
        this.swiper.slideToLoop(slideIndex, 300);
      } else {
        this.swiper.slideTo(slideIndex, 300);
      }

      this.stopAutoplay();
    }

    handleBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.startAutoplay();
    }

    handleSectionUnload(event) {
      if (event.target.contains(this)) this.destroy();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    handleClick(event) {
      if (this.dataset.designMode !== 'true') return;

      const link = event.target.closest?.('.tooto-carousel-item[href]');
      if (link && this.contains(link)) event.preventDefault();
    }

    handleFocusIn() {
      this.stopAutoplay();
    }

    handleFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.startAutoplay();
    }

    stopAutoplay() {
      if (this.swiper?.autoplay?.running) this.swiper.autoplay.stop();
    }

    startAutoplay() {
      if (this.dataset.autoplay !== 'true' || document.hidden) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (this.swiper?.autoplay && !this.swiper.autoplay.running) this.swiper.autoplay.start();
    }
  }

  customElements.define('tooto-carousel', TootoCarousel);
}
